// web41_start_date_reservation — front-end-only demand test (Narek, Sep 2026).
// Nothing is persisted beyond analytics events; see docs in the design doc.

export const RESERVATION_MAX_DAYS = 90;

export const RESERVATION_PRESETS = [
  { id: "2_weeks", label: "In 2 weeks", days: 14 },
  { id: "1_month", label: "In 1 month", days: 30 },
  { id: "2_months", label: "In 2 months", days: 60 },
] as const;

export type ReservationPreset = (typeof RESERVATION_PRESETS)[number]["id"] | "custom";

// Annual amounts the pricing page shows for the Fully Committed plan: the
// anchor price while the timed offer is live/revived, the regular (÷0.8) price
// once it expires. Kept in sync with LandingPage's plans by a unit test (this
// module must not import LandingPage — the pricing components import it).
export const ANNUAL_ANCHOR_AMOUNT = 189;
export const ANNUAL_REGULAR_AMOUNT = 236.25;

export function annualPriceLabel(isDiscountActive: boolean): string {
  const amount = isDiscountActive ? ANNUAL_ANCHOR_AMOUNT : ANNUAL_REGULAR_AMOUNT;
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}/year`;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(from: Date, days: number): Date {
  const d = startOfDay(from);
  d.setDate(d.getDate() + days);
  return d;
}

export function offsetDays(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86400000);
}

// "Sep 23" — month + day, no year (the horizon is 90 days).
export function formatShortDate(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// yyyy-mm-dd in LOCAL time for <input type="date">.
export function toInputDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function fromInputDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

// A custom date must be tomorrow .. today + 90 days.
export function isValidCustomDate(date: Date, today: Date = new Date()): boolean {
  const offset = offsetDays(today, date);
  return offset >= 1 && offset <= RESERVATION_MAX_DAYS;
}
